import React from 'react'
import { Col, Icon, Row } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  AirsSummary,
  HotelsSummary,
  VehiclesSummary
} from 'src/components/organisms/'
import { OriginRequest } from 'src/core/store/ducks/requests/types'
import { Request } from 'src/types'
import currencyMask from 'src/components/Formik/InputCurrency/mask'
import useRequestTotals from 'src/core/hooks/use-request-totals'
import * as Styles from './styles'
import { WrapRow } from '../../views/Form/components/Steps/styles'
import AdvancesSummary from '../../views/Form/components/Steps/Services/components/Advances/components/AdvancesSummary'
import BillingSummary from '../../views/Form/components/Steps/Assignment/components/Billing/components/BillingSummary'
import BudgetSummary from '../../views/Form/components/Steps/Assignment/components/Budget/budget-summary'
import HistoryJustificationSummary from '../history-justification-summary'
import ItinerariesSummary from '../../views/Form/components/Steps/Itineraries/components/ItinerariesSummary'
import RequestSummaryObservation from '../request-summary-observation'
import SetupSummary from '../../views/Form/components/Steps/Setup/components/SetupSummary'
import SharingSummary from '../../views/Form/components/Steps/Assignment/components/Sharing/components/SharingSummary'

interface Props
  extends Readonly<{
    enableAdvances?: boolean
    enableAdvancesEdit?: boolean
    enableAirs?: boolean
    enableBilling?: boolean
    enableBillingEdit?: boolean
    enableBudget?: boolean
    enableEditAirs?: boolean
    enableEditHotels?: boolean
    enableEditObservation?: boolean
    enableEditVehicles?: boolean
    enableHotels?: boolean
    enableItineraries?: boolean
    enableItinerariesEdit?: boolean
    enableSharing?: boolean
    enableSharingEdit?: boolean
    enableVehicles?: boolean
    originRequest?: OriginRequest
    request: Request
    saveRequest?: (request: Request) => void
  }> {}

function RequestSummary({
  enableAdvances = true,
  enableAdvancesEdit = false,
  enableAirs = true,
  enableBilling = true,
  enableBillingEdit = false,
  enableBudget = false,
  enableEditAirs = false,
  enableEditHotels = false,
  enableEditObservation,
  enableEditVehicles = false,
  enableHotels = true,
  enableItineraries = true,
  enableItinerariesEdit = false,
  enableSharing = true,
  enableSharingEdit = false,
  enableVehicles = true,
  originRequest,
  request,
  saveRequest
}: Props) {
  const { t } = useTranslation()
  const totals = useRequestTotals(request)

  return (
    <WrapRow>
      <Col span={12}>
        <SetupSummary originRequest={originRequest} request={request} />
        <RequestSummaryObservation
          enableEditObservation={enableEditObservation}
          request={request}
          saveRequest={saveRequest}
        />
        <HistoryJustificationSummary request={request} />
        {enableItineraries && (
          <ItinerariesSummary
            enableModal={enableItinerariesEdit}
            request={request}
          />
        )}
        {enableAirs && (
          <AirsSummary
            approvals={enableEditAirs}
            originRequest={originRequest}
            request={request}
          />
        )}
        {enableHotels && (
          <HotelsSummary
            originRequest={originRequest}
            approvals={enableEditHotels}
            request={request}
          />
        )}
        {enableVehicles && (
          <VehiclesSummary
            originRequest={originRequest}
            approvals={enableEditVehicles}
            request={request}
          />
        )}
      </Col>
      <Col offset={1} span={11}>
        <Row
          align="middle"
          data-test-id="summary-totals"
          justify="space-between"
          type="flex"
        >
          <Col span={7}>
            <Styles.Card>
              <Styles.Statistic
                title={t('web.summary.total-amount')}
                value={`BRL ${
                  currencyMask({
                    formatStr: true,
                    value: totals.advances
                  }).maskedValue
                }`}
                precision={2}
                valueStyle={{ color: '#24b550' }}
                prefix={<Icon type="arrow-up" />}
              />
            </Styles.Card>
          </Col>
          <Col offset={1} span={7}>
            <Styles.Card>
              <Styles.Statistic
                title={t('web.summary.total-spent')}
                value={`BRL ${
                  currencyMask({
                    formatStr: true,
                    value: totals.expenses
                  }).maskedValue
                }`}
                precision={2}
                valueStyle={{ color: '#cf1322' }}
                prefix={<Icon type="arrow-down" />}
              />
            </Styles.Card>
          </Col>
          <Col offset={1} span={7}>
            <Styles.Card>
              <Styles.Statistic
                title={t('web.summary.total-request')}
                value={`BRL ${
                  currencyMask({
                    formatStr: true,
                    value: totals.total
                  }).maskedValue
                }`}
                precision={2}
                valueStyle={{
                  color: totals.total >= 0 ? '#24b550' : '#cf1322'
                }}
                prefix={
                  <Icon type={totals.total >= 0 ? 'arrow-up' : 'arrow-down'} />
                }
              />
            </Styles.Card>
          </Col>
        </Row>
        <Row
          align="middle"
          data-test-id="summary-totals"
          justify="center"
          type="flex"
        >
          <Col span={7}>
            <Styles.Card>
              <Styles.Statistic
                title={t('web.summary.total-services')}
                value={`BRL ${
                  currencyMask({
                    formatStr: true,
                    value: totals.totalServices
                  }).maskedValue
                }`}
                precision={2}
                valueStyle={{ color: '#cf1322' }}
                prefix={<Icon type="arrow-down" />}
              />
            </Styles.Card>
          </Col>
          <Col offset={1} span={7}>
            <Styles.Card>
              <Styles.Statistic
                title="Total despesas"
                value={`BRL ${
                  currencyMask({
                    formatStr: true,
                    value: totals.totalExpenses
                  }).maskedValue
                }`}
                precision={2}
                valueStyle={{ color: '#cf1322' }}
                prefix={<Icon type="arrow-down" />}
              />
            </Styles.Card>
          </Col>
        </Row>
        {enableBudget && <BudgetSummary request={request} />}
        {enableSharing && (
          <SharingSummary enableModal={enableSharingEdit} request={request} />
        )}
        {enableBilling && (
          <BillingSummary enableModal={enableBillingEdit} request={request} />
        )}
        {enableAdvances && (
          <AdvancesSummary enableModal={enableAdvancesEdit} request={request} />
        )}
      </Col>
    </WrapRow>
  )
}

export default RequestSummary
